import { useEffect, useState } from 'react';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { useSnackbar } from 'notistack';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { TenantSlugDiscoveryResponse } from 'api';
import { IS_LOGGING_OUT_KEY, LOGGING_OUT_VALUE, SESSION_EXPIRED_MESSAGE, SIGN_IN_VIEW } from 'common/constants/auth';
import { useIsUserLoggedIn, useTenantDiscovery, useVerifyAccessToken } from 'pages/Auth/hooks';
import { buildSignInUrl, getStoredAccessToken } from 'pages/Auth/utils';

interface CustomRouteElementProps {
  component?: ComponentType;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: ReactNode }>
    | ((props: { children: ReactNode }) => ReactElement);
}

// Helper component that returns the element for use in Route components
export const CustomRouteElement = (props: CustomRouteElementProps) => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const verifyAccessToken = useVerifyAccessToken();
  const { enqueueSnackbar } = useSnackbar();

  // Prevents the discovery effect below from re-firing once the SSO-vs-normal-login flow is resolved.
  const [isPendingFlowResolution, setIsPendingFlowResolution] = useState(true);

  const { discoverTenantBySlug, needsDiscovery } = useTenantDiscovery({ location: globalThis.location.href });

  const [hasVerifiedToken, setHasVerifiedToken] = useState(false);
  const accessToken = getStoredAccessToken();
  const location = useLocation();
  const isLoggingOut = localStorage.getItem(IS_LOGGING_OUT_KEY) === LOGGING_OUT_VALUE;

  const { layout: Layout, component: Component, isPrivate = false } = props;

  const hasAccessToken = Boolean(accessToken);

  useEffect(() => {
    // Token storage can outlive in-memory user state after refreshes, so verify before redirecting.
    if (hasAccessToken && !isUserLoggedIn && !hasVerifiedToken) {
      let isMounted = true;

      const verifyStoredSession = async () => {
        try {
          await verifyAccessToken();
        } finally {
          if (isMounted) {
            if (!getStoredAccessToken() && !isLoggingOut) {
              enqueueSnackbar(SESSION_EXPIRED_MESSAGE, {
                variant: 'info',
              });
            }
            setHasVerifiedToken(true);
          }
        }
      };

      verifyStoredSession();

      return () => {
        isMounted = false;
      };
    }

    return undefined;
  }, [enqueueSnackbar, hasAccessToken, hasVerifiedToken, isLoggingOut, isUserLoggedIn, verifyAccessToken]);

  useEffect(() => {
    if (!hasAccessToken) {
      setHasVerifiedToken(false);
    }
  }, [hasAccessToken]);

  useEffect(() => {
    const verifyFlowToFollow = async () => {
      const flow = await discoverTenantBySlug();
      const postDiscoveryFlow = flow?.post_discovery_flow as string | undefined;
      if (flow && postDiscoveryFlow === TenantSlugDiscoveryResponse.post_discovery_flow.SAML) {
        // browser redirect to SSO login URL
        globalThis.location.href = flow.login_url as string;
      } else {
        setIsPendingFlowResolution(false);
      }
    };
    if (!isUserLoggedIn && !hasAccessToken && needsDiscovery && isPendingFlowResolution && !isLoggingOut) {
      verifyFlowToFollow();
    }
  }, [needsDiscovery, hasAccessToken, discoverTenantBySlug, isPendingFlowResolution, isUserLoggedIn, isLoggingOut]);

  useEffect(() => {
    // This executes after we've been redirected to logIn,
    // so we can remove the helper flag
    if (isLoggingOut) {
      localStorage.removeItem(IS_LOGGING_OUT_KEY);
    }
  }, [isLoggingOut]);

  // Protect private page from unauthorized users
  if (!isUserLoggedIn && !accessToken && isPrivate) {
    return (
      <Navigate
        to={buildSignInUrl({
          // Explicit logout should land on a fresh sign-in screen instead of restoring the protected page.
          returnTo: isLoggingOut ? null : `${location.pathname}${location.search}`,
          view: SIGN_IN_VIEW.IDENTIFIER,
        })}
        replace
      />
    );
  }

  if (Layout && Component) {
    return (
      <Layout>
        <Component />
      </Layout>
    );
  }

  if (Layout) {
    return (
      <Layout>
        <Outlet />
      </Layout>
    );
  }

  return Component ? <Component /> : <></>;
};

// Legacy interface for backward compatibility
interface CustomRouteProps {
  path: string;
  component?: ComponentType;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: ReactNode }>
    | ((props: { children: ReactNode }) => ReactElement);
  exact?: boolean;
}

// This is kept for compatibility but should not be used as direct child of Routes
const CustomRoute = (props: CustomRouteProps) => (
  // This component is deprecated in React Router v6 context
  // Use CustomRouteElement instead
  <CustomRouteElement {...props} />
);
export default CustomRoute;
